import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { checkoutStart, publicValidateDiscount } from "../lib/api";

/**
 * Pricing page (2026-02-04 · PM31 v2 BRD)
 *
 * Owner-locked decisions applied here:
 *  - v2 public tiers: Starter $197 · Pro $497 · Crew $997 · Enterprise (from $2,500 · contact-only).
 *  - Positioning: "Replace your $50K office manager" — NOT "answering service".
 *  - Grandfathered tiers (ARIA $97, Bronze $49, Platinum $149, Elite, Market Leader,
 *    old ARIA Pro/Front Office) never render here. Existing subscribers stay on
 *    their locked-in rate; this page only sells v2.
 *  - Annual toggle: 10 months for 12 (~17% off) per BRD §2.
 *  - Overage $0.75/answered call above allowance, auto-billed, one-tap upgrade at 80%.
 */

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const INK  = "#1A1A2E";
const MUTED = "#5a5d66";
const BG = "#F8F9FA";

const PLANS = [
  {
    id: "v2_starter",
    annualId: "v2_starter_annual",
    name: "Starter",
    monthly: 197,
    annual: 1970,
    calls: 150,
    buyerLine: "Solo op or 1–2 trucks. You still answer the phone yourself.",
    accent: false,
    dominant: false,
    items: [
      "ARIA answers every missed call on your existing number, 24/7",
      "150 answered calls included / month",
      "Booked-Job Ledger — 48h one-tap confirmation with dollar value",
      "Weekly Receipt of confirmed booked revenue",
      "Self-serve call-forwarding setup (carrier-specific, verified by test call)",
      "Overage $0.75/answered call · one-tap upgrade at 80%",
    ],
    cta: "START MY FREE MONTH",
  },
  {
    id: "v2_pro",
    annualId: "v2_pro_annual",
    name: "Pro",
    monthly: 497,
    annual: 4970,
    calls: 400,
    buyerLine: "3–8 trucks. Quotes going cold and Google reviews are thin.",
    accent: true,
    dominant: true,
    badge: "REPLACES YOUR $50K OFFICE MANAGER",
    items: [
      "Everything in Starter",
      "400 answered calls included / month",
      "Estimate Follow-Up Engine — day 2 / 5 / 12 / 30, voice escalation over threshold",
      "Maintenance Agreement renewals — ARIA works your book before it lapses",
      "Review Generation — post-job review requests routed to Google",
      "White-glove onboarding call within 2 hours of signup",
    ],
    cta: "START MY FREE MONTH",
  },
  {
    id: "v2_crew",
    annualId: "v2_crew_annual",
    name: "Crew",
    monthly: 997,
    annual: 9970,
    calls: 1000,
    buyerLine: "Multi-truck shop with real AR and a full pipeline.",
    accent: false,
    dominant: false,
    items: [
      "Everything in Pro",
      "1,000 answered calls included / month",
      "Dormant Customer Reactivation — 12–24 month idle-customer campaigns",
      "AR Collections — polite escalating follow-up with an embedded payment link",
      "Multi-trade coverage · priority routing on emergency calls",
      "Quarterly revenue review with named account manager",
      "One recovered $9K aged invoice pays for the whole year.",
    ],
    cta: "START MY FREE MONTH",
  },
  {
    id: "v2_enterprise",
    annualId: null,
    name: "Enterprise",
    monthly: 2500,
    fromLabel: "from",
    calls: null,
    buyerLine: "Multi-location, franchise, or 10+ trucks.",
    accent: false,
    dominant: false,
    anchor: true,
    items: [
      "Brand → location → user hierarchy · roll-up reporting",
      "Per-location billing · role-based permissions",
      "Custom call allowance · optional outcome billing (2% of recovered revenue)",
      "Dedicated onboarding manager + weekly ops sync during rollout",
      "SLA · SSO · security review · custom SLAs",
      "Franchise system referral incentives available",
    ],
    ctaAnchor: '"Every requirement in this doc is compatible with a $15M exit or a $12M ARR raise. This tier is where you decide."',
    cta: "TALK TO US",
  },
];

const PricingPage = () => {
  const nav = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const [email, setEmail] = useState("");
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // 'monthly' | 'annual'
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get("code")?.toUpperCase() || "");
  const [codeStatus, setCodeStatus] = useState(null);
  const [error, setError] = useState(null);

  const highlightedTier = new URLSearchParams(window.location.search).get("highlight");
  const warmContext = new URLSearchParams(window.location.search).get("warm");

  // Legacy highlight params from the homepage quiz map onto the new v2 tiers.
  const legacyToV2 = { growth: "v2_starter", aria_pro: "v2_pro", front_office: "v2_crew" };
  const effectiveHighlight = legacyToV2[highlightedTier] || highlightedTier;

  useEffect(() => {
    if (!effectiveHighlight) return;
    const el = document.querySelector(`[data-testid="plan-${effectiveHighlight}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "transform .3s ease";
      el.style.transform = "translateY(-16px)";
      setTimeout(() => { el.style.transform = ""; }, 900);
    }
  }, [effectiveHighlight]);

  useEffect(() => {
    if (!code) { setCodeStatus(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await publicValidateDiscount(code.trim().toUpperCase(), "v2_starter");
        if (!cancelled) setCodeStatus(r);
      } catch {
        if (!cancelled) setCodeStatus({ valid: false, message: "Couldn't check that code." });
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const start = async (planKey, plan) => {
    setError(null);
    if (planKey.startsWith("v2_enterprise")) {
      window.location.href = "/contact?plan=v2_enterprise";
      return;
    }
    if (!email) {
      setError("Enter your email above to continue.");
      return;
    }
    setBusyId(planKey);
    try {
      const payload = { plan: planKey, email, is_founding: false };
      if (code && codeStatus?.valid) payload.discount_code = code.trim().toUpperCase();
      const res = await checkoutStart(payload);
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      nav(`/welcome?session=${res.session_id}&plan=${planKey}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.toLowerCase().includes("price id")) {
        setError("This tier is being activated. Email contact@nearnow.biz and we'll onboard you personally.");
      } else {
        setError(detail || e?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const legacyHighlightCopy = useMemo(() => {
    const name = PLANS.find((p) => p.id === effectiveHighlight)?.name;
    return name || "Pro";
  }, [effectiveHighlight]);

  return (
    <div data-testid="pricing-page" style={{ background: BG, padding: "40px 20px 80px" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <p style={eyebrow}>NEARNOW PRICING · CANCEL ANYTIME</p>
        <h1 className="font-display" data-testid="pricing-headline" style={h1}>
          REPLACE YOUR $50K OFFICE MANAGER.
        </h1>
        <p style={subhead}>
          A full-time office person costs $45,000 a year, takes vacation, and goes home at 5.<br />
          <strong style={{ color: NAVY }}>This doesn&apos;t. And your free month starts the day your calls forward — not the day you sign up.</strong>
        </p>

        {warmContext && effectiveHighlight && (
          <div data-testid="pricing-warm-banner" style={{
            maxWidth: 620, margin: "18px auto 0", padding: "12px 16px",
            background: "#0A1628", color: "#fff", borderRadius: 10, textAlign: "center",
            border: `1px solid ${GOLD}`,
          }}>
            <span style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2, fontSize: 12 }}>
              FROM YOUR HEALTH SCORE
            </span>
            <div style={{ marginTop: 4, fontSize: 15 }}>
              Based on what you told us — <strong style={{ color: GOLD }}>{legacyHighlightCopy}</strong> is the plan built for your bleed.
            </div>
          </div>
        )}

        {/* Billing period toggle */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
          <div
            role="tablist"
            aria-label="Billing period"
            data-testid="billing-toggle"
            style={{
              display: "inline-flex", background: "#fff", borderRadius: 999,
              border: "1px solid #e5e7ee", padding: 4,
              boxShadow: "0 2px 6px rgba(10,22,40,0.06)",
            }}
          >
            <ToggleBtn active={billingPeriod === "monthly"} onClick={() => setBillingPeriod("monthly")} testid="toggle-monthly">
              MONTHLY
            </ToggleBtn>
            <ToggleBtn active={billingPeriod === "annual"} onClick={() => setBillingPeriod("annual")} testid="toggle-annual">
              ANNUAL <span style={{ marginLeft: 6, fontSize: 10, color: billingPeriod === "annual" ? NAVY : GOLD }}>SAVE 17%</span>
            </ToggleBtn>
          </div>
        </div>

        {/* Email + discount */}
        <div style={{ maxWidth: 520, margin: "22px auto 0" }}>
          <label className="nn-label" style={{ textAlign: "center", display: "block" }}>Your email</label>
          <input
            data-testid="pricing-email"
            className="nn-input" type="email" placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />

          <label className="nn-label" style={{ textAlign: "center", display: "block", marginTop: 14 }}>
            Discount code <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            data-testid="pricing-discount-code"
            className="nn-input"
            type="text"
            placeholder="TT1 / HD10 / LA1"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            style={{ textTransform: "uppercase", letterSpacing: 2 }}
          />
          {code && codeStatus && (
            codeStatus.valid ? (
              <div data-testid="discount-valid" style={okBox}>
                ✓ {codeStatus.discount_pct}% off via <strong>{codeStatus.source_label}</strong> — saves you ${(codeStatus.discount_cents / 100).toFixed(0)} on your Starter cycle.
              </div>
            ) : (
              <div data-testid="discount-invalid" style={errBox}>
                ✗ {codeStatus.message}
              </div>
            )
          )}
          {error && <div data-testid="pricing-error" style={errBox}>{error}</div>}
        </div>

        {/* 4-column pricing grid */}
        <div data-testid="pricing-grid" style={grid}>
          {PLANS.map((p) => {
            const isAnnual = billingPeriod === "annual" && p.annualId;
            const displayPlanKey = isAnnual ? p.annualId : p.id;
            const displayPrice = isAnnual ? p.annual : p.monthly;
            const priceUnit = isAnnual ? "/yr" : "/mo";
            return (
              <div
                key={p.id}
                data-testid={`plan-${displayPlanKey}`}
                style={p.dominant ? cardDominant : (p.anchor ? cardAnchor : cardBase)}
              >
                {p.badge && (
                  <div style={dominantBadge} data-testid={`plan-${p.id}-badge`}>
                    {p.badge}
                  </div>
                )}
                <div className="font-display" data-testid={`plan-${p.id}-name`} style={{
                  fontSize: 40, letterSpacing: 1.5,
                  color: p.dominant ? GOLD : NAVY, lineHeight: 1, marginTop: p.badge ? 12 : 0,
                }}>
                  {p.name.toUpperCase()}
                </div>

                <div data-testid={`plan-${p.id}-buyer`} style={{
                  fontSize: 14, color: p.dominant ? "#cbd0db" : MUTED,
                  marginTop: 10, fontStyle: "italic", minHeight: 42,
                }}>
                  {p.buyerLine}
                </div>

                <div data-testid={`plan-${p.id}-price`} style={{
                  fontFamily: "Bebas Neue, sans-serif", fontSize: 62, lineHeight: 1,
                  color: p.dominant ? "#fff" : NAVY, marginTop: 16,
                  display: "flex", alignItems: "flex-end", gap: 6,
                }}>
                  {p.fromLabel && (
                    <span style={{ fontSize: 16, color: p.dominant ? "#cbd0db" : MUTED, fontFamily: "system-ui", marginBottom: 12 }}>
                      {p.fromLabel}
                    </span>
                  )}
                  ${displayPrice.toLocaleString()}
                  <span style={{ fontSize: 18, color: p.dominant ? "#cbd0db" : MUTED, marginBottom: 8 }}>{priceUnit}</span>
                </div>
                {isAnnual && (
                  <div style={{ fontSize: 12, color: p.dominant ? GOLD : "#1A7A4A", marginTop: 4 }}>
                    Saves ${(p.monthly * 12 - p.annual).toLocaleString()}/yr — 10 months for 12.
                  </div>
                )}
                {p.calls != null && (
                  <div data-testid={`plan-${p.id}-calls`} style={{
                    marginTop: 8, fontSize: 13,
                    color: p.dominant ? "#cbd0db" : MUTED,
                  }}>
                    {p.calls.toLocaleString()} answered calls / month · overage $0.75
                  </div>
                )}

                <ul style={{ paddingLeft: 0, listStyle: "none", marginTop: 16 }}>
                  {p.items.map((it, i) => (
                    <li key={i} style={{
                      padding: "8px 0", fontSize: 14,
                      color: p.dominant ? "#fff" : INK,
                      borderBottom: p.dominant ? "1px solid #1a2640" : "1px solid #eef0f4",
                      lineHeight: 1.4,
                    }}>
                      <span style={{ color: p.dominant ? GOLD : "#1A7A4A", marginRight: 8, fontWeight: 700 }}>✓</span>
                      {it}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-${p.id}-cta`}
                  className={p.dominant ? "btn-gold tap" : "btn-navy tap"}
                  style={{ width: "100%", marginTop: 20 }}
                  disabled={busyId === displayPlanKey}
                  onClick={() => start(displayPlanKey, p)}
                >
                  {busyId === displayPlanKey ? "..." : p.cta}
                </button>

                {p.ctaAnchor && (
                  <p style={{ fontSize: 13, color: MUTED, textAlign: "center", marginTop: 12, fontStyle: "italic", lineHeight: 1.4 }}>
                    {p.ctaAnchor}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Trial + guarantee reassurance */}
        <div data-testid="pricing-guarantee-strip" style={guaranteeStrip}>
          <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2, color: GOLD, fontSize: 22, marginBottom: 6 }}>
            THE GUARANTEE
          </div>
          <div style={{ fontSize: 17, color: "#fff", maxWidth: 780, margin: "0 auto", lineHeight: 1.5 }}>
            Free until your calls are forwarding. Starter self-serves the forwarding setup with carrier-specific instructions and a verification test call.
            Pro and above get a real human call from us within 2 hours of signup.
            If ARIA doesn&apos;t answer at least <strong style={{ color: GOLD }}>10 calls you would have missed</strong> in your first 30 days, month two is free.
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 30 }}>
          <Link to="/onboard-simple" data-testid="pricing-onboard-link" style={{ color: NAVY, fontSize: 17 }}>
            New here? Take the 5-minute setup first →
          </Link>
        </div>
      </div>
    </div>
  );
};

const ToggleBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    style={{
      background: active ? NAVY : "transparent",
      color: active ? GOLD : NAVY,
      border: "none",
      padding: "10px 22px",
      borderRadius: 999,
      fontFamily: "'Bebas Neue', sans-serif",
      letterSpacing: 2,
      fontSize: 14,
      cursor: "pointer",
      minHeight: 44,
    }}
  >
    {children}
  </button>
);

// ----- styles -----
const eyebrow = { color: GOLD, fontSize: 16, letterSpacing: 4, fontFamily: "Bebas Neue, sans-serif", margin: 0, textAlign: "center" };
const h1 = { fontSize: 60, color: NAVY, textAlign: "center", margin: "10px 0 8px", letterSpacing: 1, lineHeight: 1.02 };
const subhead = { fontSize: 18, color: MUTED, textAlign: "center", maxWidth: 780, margin: "0 auto", lineHeight: 1.55 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, marginTop: 30 };
const cardBase = { background: "#fff", color: INK, borderRadius: 14, padding: 24, border: "1px solid #e5e7ee", boxShadow: "0 2px 8px rgba(10,22,40,0.04)", position: "relative" };
const cardDominant = { background: NAVY, color: "#fff", borderRadius: 14, padding: 28, border: `3px solid ${GOLD}`, position: "relative", boxShadow: "0 20px 48px rgba(212,175,55,0.22)", transform: "translateY(-6px)" };
const cardAnchor = { background: "#fff", color: INK, borderRadius: 14, padding: 24, border: `2px solid ${NAVY}`, position: "relative", boxShadow: "0 12px 30px rgba(10,22,40,0.10)" };
const dominantBadge = { position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: GOLD, color: NAVY, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2, fontSize: 13, padding: "6px 14px", borderRadius: 999, whiteSpace: "nowrap" };
const okBox = { marginTop: 8, padding: "10px 14px", background: "#E8F5EE", color: "#1A7A4A", borderRadius: 10, fontSize: 14 };
const errBox = { marginTop: 8, padding: "10px 14px", background: "#FDECEC", color: "#A32D2D", borderRadius: 10, fontSize: 14 };
const guaranteeStrip = { marginTop: 40, background: NAVY, borderRadius: 14, padding: "26px 20px", textAlign: "center", border: `2px solid ${GOLD}` };

export default PricingPage;
